<template>
    <div v-if="candidates.length" class="candidates">
        <h4 class="candidates__title">
            {{ $t('pages.assistant.candidates.title') }}
        </h4>
        <!-- Shown even with exactly one candidate: auto-selecting the only
             match is how a wig gets attached to the wrong person. -->
        <p class="candidates__hint">
            {{ $t('pages.assistant.candidates.hint') }}
        </p>
        <ul class="candidates__list">
            <li
                v-for="candidate in candidates"
                :key="candidate.id"
                class="candidates__item"
            >
                <div class="candidates__person">
                    <span class="candidates__name" dir="auto">{{
                        fullName(candidate)
                    }}</span>
                    <span class="candidates__phone" dir="ltr">{{
                        candidate.phone
                    }}</span>
                </div>
                <v-btn
                    small
                    dark
                    color="#967adc"
                    :disabled="isSending"
                    @click="choose(candidate)"
                >
                    {{ $t('pages.assistant.candidates.select') }}
                </v-btn>
            </li>
        </ul>
    </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import {
    assistantActionTypes,
    assistantGetterTypes,
} from '@/store/modules/assistant/types'

export default {
    name: 'AssistantClientCandidates',
    computed: {
        ...mapGetters('assistant', {
            candidates: assistantGetterTypes.GET_CLIENT_CANDIDATES,
            isSending: assistantGetterTypes.GET_IS_SENDING,
        }),
    },
    methods: {
        ...mapActions('assistant', {
            selectClient: assistantActionTypes.SELECT_CLIENT,
        }),
        fullName: function ({ name, surname }) {
            return [name, surname].filter((part) => part).join(' ')
        },
        /**
         * Say who was picked, not that someone was.
         *
         * The binding is the id in `confirm`, so the words carry no weight for
         * the backend — they carry it for the manager reading the transcript
         * afterwards. "That client, please." tells them nothing about which of
         * the two people on screen the wig went to. The phone comes along when
         * there is one, because candidates are usually the same name twice.
         */
        choose: function (candidate) {
            const name = this.fullName(candidate)

            // The text is required, not decoration: a turn carries text or
            // audio, and its language is what the assistant answers in.
            this.selectClient({
                clientId: candidate.id,
                text: this.$t('pages.assistant.candidates.sentText', {
                    client: candidate.phone
                        ? `${name}, ${candidate.phone}`
                        : name,
                }),
            })
        },
    },
}
</script>

<style scoped lang="scss">
.candidates {
    padding: 12px 16px;
    background: #f7f7f9;
    border-top: 1px solid #eceeef;

    &__title {
        font-size: 14px;
        font-weight: 600;
        margin: 0 0 2px;
    }

    &__hint {
        margin: 0 0 8px;
        font-size: 12px;
        color: #8a8f94;
    }

    &__list {
        list-style: none;
        padding: 0;
        margin: 0;
    }

    &__item {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 8px;
        padding: 6px 0;
        border-bottom: 1px solid #eceeef;

        &:last-child {
            border-bottom: none;
        }
    }

    &__person {
        display: flex;
        flex-direction: column;
        min-width: 0;
    }

    &__name {
        font-size: 14px;
    }

    // `dir="ltr"` keeps the digits in order but would also drag them to the
    // left edge, away from the name above.
    &__phone {
        font-size: 12px;
        color: #8a8f94;

        @include for-hebrew {
            text-align: right;
        }
    }
}
</style>
